import { Lock, LockOpen } from "lucide-react";
import type { GroupLockState, GroupStatus, GroupIhqType, RatingSource, UserRole } from "../types";
import { GROUP_STATUS_SHORT_LABELS, groupLockReason, isGroupLocked } from "../types";

interface StatusPillProps {
  status: GroupStatus;
  /** Show the full Quote Status text instead of the short label. */
  full?: boolean;
  children?: never;
}

const STATUS_CLASSES: Record<GroupStatus, string> = {
  "Ready for UW PreScreen": "bg-gray-100 text-gray-700",
  "AI First Initial Score Completed": "bg-info-bg text-info",
  "PreScreen - WIP - Working": "bg-warning-bg text-gray-800",
  "Preliminary Prescreen Completed": "bg-success-bg text-success",
  "Renewal Ready": "bg-brand-green-10 text-brand-green-dark",
};

/** Workbench Status pill. The label is the workbench wording (the Salesforce stage when `full`); the tooltip names the Salesforce Quote Status it is written to. */
export function StatusPill({ status, full = false }: StatusPillProps) {
  return (
    <span title={`Salesforce Quote Status: ${status}`} className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_CLASSES[status]}`}>
      {full ? status : GROUP_STATUS_SHORT_LABELS[status]}
    </span>
  );
}

interface LockPillProps {
  group: GroupLockState;
  /** Icon only, for grid cells; the reason stays in the tooltip and for screen readers. */
  compact?: boolean;
  children?: never;
}

/** Locked or open, with why (feedback 1.3). */
export function LockPill({ group, compact = false }: LockPillProps) {
  const locked = isGroupLocked(group);
  const reason = groupLockReason(group);
  const Icon = locked ? Lock : LockOpen;
  const tone = locked ? "text-gray-500" : "text-success";
  if (compact) {
    return (
      <span title={reason} className={`inline-flex items-center ${tone}`}>
        <Icon size={14} aria-hidden="true" />
        <span className="sr-only">{reason}</span>
      </span>
    );
  }
  return (
    <span title={reason} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${locked ? "border-gray-300 bg-gray-50 text-gray-600" : "border-success/30 bg-success-bg text-success"}`}>
      <Icon size={12} aria-hidden="true" />
      {locked ? "Locked" : "Open"}
    </span>
  );
}

interface VersionPillProps {
  version: number;
  isSuperseded: boolean;
  children?: never;
}

/** "v2" pill; superseded versions are muted and say so in the tooltip. */
export function VersionPill({ version, isSuperseded }: VersionPillProps) {
  return (
    <span title={isSuperseded ? `Version ${version}, replaced by a newer version` : `Version ${version}`} className={`inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-xs ${isSuperseded ? "border-gray-200 text-gray-400 line-through" : "border-gray-300 text-gray-700"}`}>
      v{version}
    </span>
  );
}

interface IhqTypePillProps {
  ihqType: GroupIhqType;
  children?: never;
}

const IHQ_PILL_TONES: Record<GroupIhqType, string> = {
  Long: "bg-brand-navy text-white",
  Short: "bg-brand-green-10 text-brand-green-dark",
  Unknown: "border border-dashed border-gray-400 text-gray-600",
};

export function IhqTypePill({ ihqType }: IhqTypePillProps) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${IHQ_PILL_TONES[ihqType]}`} title={ihqType === "Unknown" ? "Form type not known since migration; RMM uses the Long table until set on Edit Group" : undefined}>
      {ihqType}
    </span>
  );
}

interface RoleBadgeProps {
  role: UserRole;
  children?: never;
}

export function RoleBadge({ role }: RoleBadgeProps) {
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${role === "Actuarial" ? "border-brand-navy text-brand-navy" : "border-brand-green text-brand-green-dark"}`}>{role}</span>;
}

interface SourcePillProps {
  source: RatingSource;
  children?: never;
}

const SOURCE_LABELS: Record<RatingSource, string> = {
  Skynet: "Skynet",
  Manual: "Manual",
  Comorbidity: "Comorbidity",
  DoubledBuild: "Doubled Build",
  DoubledHypertension: "Doubled Hypertension",
  BrandToGeneric: "Brand to Generic",
  Renewal: "Renewal",
};

export function SourcePill({ source }: SourcePillProps) {
  return <span className="inline-flex items-center whitespace-nowrap rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{SOURCE_LABELS[source]}</span>;
}

interface TagPillProps {
  label: string;
  tone?: "gray" | "green" | "navy" | "warning";
  title?: string;
  children?: never;
}

const TAG_TONES: Record<NonNullable<TagPillProps["tone"]>, string> = {
  gray: "bg-gray-100 text-gray-600",
  green: "bg-brand-green-10 text-brand-green-dark",
  navy: "bg-brand-navy text-white",
  warning: "bg-warning-bg text-gray-800",
};

export function TagPill({ label, tone = "gray", title }: TagPillProps) {
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${TAG_TONES[tone]}`} title={title}>
      {label}
    </span>
  );
}
